import { useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Trash2 } from 'lucide-react';

import { removeMyAvatar, updateMyProfile, uploadMyAvatar } from '../../api/auth';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Page, PageHeader, PageSection } from '../../components/Page';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import styles from './ProfilePage.module.css';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Профиль: имя и собственный аватар (UC-AUTH-07). */
export function ProfilePage() {
  const { user, isLoading, setUser } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.me });
  };

  const onError = (cause: unknown) => {
    const human = humanizeError(cause);
    setError(human.message);
  };

  const nameMutation = useMutation({
    mutationFn: () => updateMyProfile({ displayName: displayName.trim() }),
    onSuccess: (result) => {
      setUser(result.user);
      invalidate();
      toast.success('Имя сохранено');
    },
    onError,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadMyAvatar(file),
    onSuccess: (result) => {
      setUser(result.user);
      setError(null);
      invalidate();
      toast.success('Аватар обновлён');
    },
    onError,
  });

  const removeMutation = useMutation({
    mutationFn: () => removeMyAvatar(),
    onSuccess: (result) => {
      setUser(result.user);
      setError(null);
      invalidate();
      toast.success('Аватар удалён');
    },
    onError,
  });

  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }
  if (!user) return null;

  const onPickFile = (file: File | undefined): void => {
    if (!file) return;
    if (!AVATAR_TYPES.includes(file.type)) {
      setError('Выберите PNG, JPEG, GIF или WebP');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError('Аватар больше 5 МБ');
      return;
    }
    uploadMutation.mutate(file);
  };

  return (
    <Page width="narrow">
      <PageHeader title="Профиль" />

      <PageSection>
        <div className={styles['avatarRow']}>
          <Avatar user={user} size="md" showTooltip={false} />
          <div className={styles['avatarActions']}>
            <span className="u-muted">Фото профиля видно во всех проектах.</span>
            <div className={styles['buttons']}>
              <input
                ref={fileRef}
                className={styles['fileInput']}
                type="file"
                accept={AVATAR_TYPES.join(',')}
                aria-label="Загрузить аватар"
                onChange={(event) => {
                  onPickFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                icon={<ImagePlus size={14} />}
                loading={uploadMutation.isPending}
                onClick={() => fileRef.current?.click()}
              >
                Загрузить фото
              </Button>
              {user.avatarUrl ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={14} />}
                  loading={removeMutation.isPending}
                  onClick={() => removeMutation.mutate()}
                >
                  Удалить
                </Button>
              ) : null}
            </div>
            <span className="u-muted">PNG, JPEG, GIF или WebP до 5 МБ.</span>
          </div>
        </div>

        <Input
          label="Имя"
          value={displayName}
          maxLength={80}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        {error ? (
          <p className={styles['error']} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles['footer']}>
          <Button
            variant="primary"
            loading={nameMutation.isPending}
            disabled={displayName.trim().length === 0}
            onClick={() => {
              setError(null);
              if (displayName.trim().length === 0) {
                setError('Заполните имя');
                return;
              }
              nameMutation.mutate();
            }}
          >
            Сохранить
          </Button>
        </div>
      </PageSection>
    </Page>
  );
}
